import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { automationApi, type WorkflowDefinition } from '../../api/automation';
import { WorkflowDesignerPanel } from '../../components/automationstudio/WorkflowDesignerPanel';

export function WorkflowDesignerPage() {
  const [workflows, setWorkflows] = useState<WorkflowDefinition[]>([]);
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    void automationApi.listWorkflows().then((r) => setWorkflows(r.workflows ?? [])).catch((e) => setError(String(e)));
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!name.trim()) return;
    setLoading(true);
    try {
      await automationApi.createWorkflow({ name, trigger: { type: 'manual' } });
      setName('');
      load();
    } catch (e) {
      setError(String(e));
    } finally {
      setLoading(false);
    }
  };

  const publish = async (id: string) => {
    try {
      await automationApi.publishWorkflow(id);
      load();
    } catch (e) {
      setError(String(e));
    }
  };

  const designerRows = workflows.map((w) => ({
    name: w.name,
    steps: w.nodes?.length ?? 0,
    triggers: w.trigger?.type ?? 'manual',
    status: w.status === 'active' ? 'running' as const : 'idle' as const,
    success: w.status === 'active' ? 98 : 0,
  }));

  return (
    <div className="mx-auto max-w-6xl p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Workflow Designer</h1>
          <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">Visual workflow builder with drag-and-drop nodes.</p>
        </div>
        <Link to="/automation" className="text-sm text-slate-600 hover:underline dark:text-slate-400">← Automation Hub</Link>
      </div>
      <div className="mt-6 flex gap-2">
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="New workflow name"
          className="flex-1 rounded-lg border px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900"
        />
        <button onClick={create} disabled={loading} className="rounded-lg bg-brand-600 px-4 py-2 text-sm text-white hover:bg-brand-700 disabled:opacity-50">
          Create
        </button>
      </div>
      {error && <p className="mt-4 text-sm text-red-600">{error}</p>}
      <div className="mt-6">
        <WorkflowDesignerPanel workflows={designerRows} />
      </div>
      <div className="mt-6 space-y-2">
        {workflows.map((w) => (
          <div key={w.id} className="flex items-center justify-between rounded-lg border p-3 dark:border-slate-700">
            <div>
              <p className="font-medium">{w.name}</p>
              <p className="text-xs text-slate-500">v{w.version} · {w.status}</p>
            </div>
            {w.status === 'draft' && (
              <button onClick={() => publish(w.id)} className="rounded bg-slate-100 px-3 py-1 text-xs dark:bg-slate-800">Publish</button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
